// CSV utility functions for import/export
import {format,parse} from 'date-fns';
import {sl} from 'date-fns/locale';

// Convert number to Slovenian format (comma as decimal separator)
export const formatSlNumber=(number)=> {
  if (number===null || number===undefined || number==='') return '';
  const num=parseFloat(number);
  if (isNaN(num)) return '';
  return num.toFixed(2).replace('.',',');
};

// Convert Slovenian format to number (comma to dot)
export const parseSlNumber=(value)=> {
  if (!value || value==='') return 0;
  const numStr=value.toString().replace(',','.');
  const parsed=parseFloat(numStr);
  return isNaN(parsed) ? 0 : parsed;
};

// Format date for CSV
export const formatDateForCSV=(dateString)=> {
  if (!dateString) return '';
  try {
    return format(new Date(dateString),'dd.MM.yyyy',{locale: sl});
  } catch (error) {
    console.error('Date formatting error:',error);
    return '';
  }
};

// Parse date from CSV
export const parseDateFromCSV=(dateString)=> {
  if (!dateString) return new Date().toISOString();
  try {
    // Try different date formats
    let date;
    if (dateString.includes('.')) {
      // DD.MM.YYYY format
      const parts=dateString.split('.');
      if (parts.length===3) {
        date=new Date(parts[2],parts[1] - 1,parts[0]);
      }
    } else if (dateString.includes('-')) {
      // YYYY-MM-DD format
      date=new Date(dateString);
    } else {
      date=new Date(dateString);
    }

    // Validate date
    if (isNaN(date.getTime())) {
      throw new Error('Invalid date');
    }

    return date.toISOString();
  } catch (error) {
    console.error('Date parsing error:',error);
    return new Date().toISOString();
  }
};

// Export invoices to CSV
export const exportToCSV=(invoices,filename='racuni')=> {
  if (!invoices || invoices.length===0) {
    alert('Ni računov za izvoz');
    return;
  }

  try {
    const headers=[
      'Stranka',
      'Osnovni znesek (€)',
      'Skupni znesek (€)',
      'Interval',
      'Naslednje plačilo',
      'Opis',
      'Opombe',
      'Dodatne storitve'
    ];

    const csvData=invoices.map(invoice=> {
      // Format line items as "service1: price1;service2: price2"
      const lineItemsText=invoice.lineItems && invoice.lineItems.length > 0
        ? invoice.lineItems.map(item=> 
            `${item.service || ''}: ${formatSlNumber(item.price || 0)}€`
          ).join(';')
        : '';

      return [
        invoice.stranka || '',
        formatSlNumber(invoice.amount || 0),
        formatSlNumber(invoice.totalAmount || invoice.total_amount || invoice.amount || 0),
        getIntervalLabel(invoice.interval),
        formatDateForCSV(invoice.nextDue || invoice.next_due),
        invoice.description || '',
        invoice.notes || '',
        lineItemsText
      ];
    });

    const csvContent=[headers,...csvData]
      .map(row=> row.map(cell=> `"${(cell || '').toString().replace(/"/g,'""')}"`).join(','))
      .join('\n');

    // Add BOM for proper encoding in Excel
    const BOM='\uFEFF';
    const blob=new Blob([BOM + csvContent],{type: 'text/csv;charset=utf-8;'});
    const link=document.createElement('a');
    if (link.download !==undefined) {
      const url=URL.createObjectURL(blob);
      link.setAttribute('href',url);
      link.setAttribute('download',`${filename}_${format(new Date(),'yyyy-MM-dd')}.csv`);
      link.style.visibility='hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  } catch (error) {
    console.error('CSV export error:',error);
    alert('Napaka pri izvozu CSV: ' + error.message);
  }
};

// Parse CSV content to invoices
export const parseCSV=(csvContent)=> {
  try {
    // Clean the content - remove BOM if present and normalize line endings
    const cleanContent=csvContent
      .replace(/^\uFEFF/,'')
      .replace(/\r\n/g,'\n')
      .replace(/\r/g,'\n');

    const lines=cleanContent.split('\n').filter(line=> line.trim());

    if (lines.length < 2) {
      throw new Error('CSV datoteka mora imeti vsaj eno vrstico podatkov');
    }

    // Parse header to determine column positions
    const headerLine=lines[0];
    const headers=parseCSVLine(headerLine);
    console.log('CSV Headers:',headers);

    // Skip header row
    const dataLines=lines.slice(1);
    const invoices=[];

    dataLines.forEach((line,index)=> {
      try {
        const fields=parseCSVLine(line);
        console.log(`Line ${index + 2} fields:`,fields);

        if (fields.length < 4) {
          console.warn(`Vrstica ${index + 2}: Premalo polj (${fields.length})`);
          return;
        }

        // Map fields based on expected structure
        const [
          stranka='',
          amountStr='0',
          totalAmountStr='',
          intervalStr='Mesečno',
          nextDueStr='',
          description='',
          notes='',
          lineItemsStr=''
        ]=fields;

        // Parse line items
        const lineItems=[];
        if (lineItemsStr && lineItemsStr.trim()) {
          const items=lineItemsStr.split(';');
          items.forEach(item=> {
            const match=item.trim().match(/^(.+?):\s*([0-9,]+)\s*€?\s*$/);
            if (match) {
              lineItems.push({
                id: Date.now() + Math.random(),
                service: match[1].trim(),
                price: parseSlNumber(match[2]).toString()
              });
            }
          });
        }

        // Use totalAmount if available, otherwise use amount
        const finalTotalAmount=totalAmountStr ? parseSlNumber(totalAmountStr) : parseSlNumber(amountStr);
        const baseAmount=parseSlNumber(amountStr);

        const invoice={
          id: Date.now().toString() + Math.random().toString(36).substr(2,9),
          stranka: stranka.trim(),
          amount: baseAmount,
          totalAmount: finalTotalAmount,
          interval: parseInterval(intervalStr),
          nextDue: parseDateFromCSV(nextDueStr),
          description: description.trim(),
          notes: notes.trim(),
          lineItems: lineItems,
          createdAt: new Date().toISOString(),
          lastPaid: null
        };

        if (invoice.stranka) {
          invoices.push(invoice);
          console.log('Added invoice:',invoice);
        } else {
          console.warn(`Vrstica ${index + 2}: Manjka ime stranke`);
        }
      } catch (error) {
        console.warn(`Napaka pri obdelavi vrstice ${index + 2}:`,error.message);
      }
    });

    console.log('Final invoices:',invoices);

    if (invoices.length===0) {
      throw new Error('Ni veljavnih računov za uvoz');
    }

    return invoices;
  } catch (error) {
    console.error('CSV parsing error:',error);
    throw new Error(`Napaka pri branju CSV datoteke: ${error.message}`);
  }
};

// Improved CSV line parser that handles quoted fields and different separators
const parseCSVLine=(line)=> {
  const result=[];
  let current='';
  let inQuotes=false;
  let i=0;

  // Detect separator - try semicolon first, then comma
  const hasSemicolon=line.includes(';');
  const hasCommaOutsideQuotes=line.split('').some((char,index)=> {
    if (char==='"') inQuotes=!inQuotes;
    return char===',' && !inQuotes;
  });

  // Reset inQuotes for actual parsing
  inQuotes=false;
  const separator=hasSemicolon ? ';' : ',';
  console.log('Using separator:',separator,'for line:',line.substring(0,50) + '...');

  while (i < line.length) {
    const char=line[i];

    if (char==='"') {
      if (inQuotes && line[i + 1]==='"') {
        // Escaped quote
        current +='"';
        i +=2;
        continue;
      }
      inQuotes=!inQuotes;
    } else if (char===separator && !inQuotes) {
      result.push(current.trim());
      current='';
    } else {
      current +=char;
    }
    i++;
  }

  result.push(current.trim());

  // Clean up quoted fields
  return result.map(field=> {
    if (field.startsWith('"') && field.endsWith('"')) {
      return field.slice(1,-1).replace(/""/g,'"');
    }
    return field;
  });
};

// Convert interval from Slovenian to English
const parseInterval=(intervalStr)=> {
  const intervalMap={
    'Mesečno': 'monthly',
    'Četrtletno': 'quarterly',
    'Polletno': 'half-yearly',
    'Letno': 'yearly'
  };
  return intervalMap[intervalStr] || 'monthly';
};

// Get interval label in Slovenian
const getIntervalLabel=(interval)=> {
  const intervalMap={
    'monthly': 'Mesečno',
    'quarterly': 'Četrtletno',
    'half-yearly': 'Polletno',
    'yearly': 'Letno'
  };
  return intervalMap[interval] || 'Mesečno';
};